import React from 'react';
import { lazy, Suspense } from 'react';
import { Card, CardContent } from '@/components/ui/card';

/**
 * Lazy-loaded Entity Dashboard Components
 *
 * These components are loaded on-demand when needed,
 * improving initial page load performance
 */

// Loading component for lazy-loaded dashboards
export const DashboardLoadingFallback = () => (
  <div className="flex items-center justify-center min-h-96">
    <Card className="w-full max-w-md">
      <CardContent className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <h3 className="text-lg font-semibold mb-2">Loading Dashboard</h3>
        <p className="text-muted-foreground">Please wait while we load your dashboard...</p>
      </CardContent>
    </Card>
  </div>
);

// Lazy load all entity dashboards
const LazyBoardDashboard = lazy(() =>
  import('../../entities/community_sub-elects/BoardDashboard').then((module) => ({
    default: module.BoardDashboard,
  })),
);

const LazyVendorDashboard = lazy(() =>
  import('../../entities/vendor/VendorDashboard').then((module) => ({
    default: module.VendorDashboard,
  })),
);

const LazyManagerDashboard = lazy(() =>
  import('../../entities/community_sub-elects/BoardDashboard').then((module) => ({
    default: module.BoardDashboard,
  })),
);

const LazyResidentDashboard = lazy(() =>
  import('../../entities/resident/ResidentDashboard').then((module) => ({
    default: module.ResidentDashboard,
  })),
);

const LazySubpanelElectDashboard = lazy(() =>
  import('../../entities/community_subpanel-elect/SubpanelElectDashboard').then((module) => ({
    default: module.SubpanelElectDashboard,
  })),
);

const LazyBankDashboard = lazy(() =>
  import('../../entities/bank/BankDashboard').then((module) => ({ default: module.BankDashboard })),
);

// Pre-wrapped lazy dashboard components with Suspense
export const BoardDashboard = () => (
  <Suspense fallback={<DashboardLoadingFallback />}>
    <LazyBoardDashboard />
  </Suspense>
);

export const VendorDashboard = () => (
  <Suspense fallback={<DashboardLoadingFallback />}>
    <LazyVendorDashboard />
  </Suspense>
);

export const ManagerDashboard = () => (
  <Suspense fallback={<DashboardLoadingFallback />}>
    <LazyManagerDashboard />
  </Suspense>
);

export const ResidentDashboard = () => (
  <Suspense fallback={<DashboardLoadingFallback />}>
    <LazyResidentDashboard />
  </Suspense>
);

export const SubpanelElectDashboard = () => (
  <Suspense fallback={<DashboardLoadingFallback />}>
    <LazySubpanelElectDashboard />
  </Suspense>
);

// Alias for backward compatibility
export const PlatformAdminDashboard = SubpanelElectDashboard;

export const BankDashboard = (props: { bankId: string }) => (
  <Suspense fallback={<DashboardLoadingFallback />}>
    <LazyBankDashboard {...props} />
  </Suspense>
);
